<template>
  <div class="mt-1 w-[23rem]">
    <VeeForm v-slot="{ handleSubmit, meta }" as="">
      <form autocomplete="off" @submit="handleSubmit($event, onSubmit)">
        <div
          class="bg-neutral-secondary rounded-md p-4 max-h-[420px] h-full overflow-scroll"
        >
          <VeeField
            v-slot="{ field, errorMessage }"
            name="brokerBank"
            rules="required"
          >
            <NText
              variant="caption1"
              weight="bold"
              class="text-neutral-secondary-press mr-3"
            >
              {{ t('common.selectSymbol') }}
            </NText>

            <div class="mt-2 mb-3">
              <NListbox
                v-model="selectedIsin"
                :placeholder="t('symbol.chooseSymbol')"
                v-bind="field"
                input-size="small"
                :error="errorMessage"
                filterable
                show-error
                required
              >
                <div
                  v-if="!isLoadingSymbols && symbols.length"
                  class="py-1 px-3"
                >
                  <NText
                    class="text-neutral-secondary"
                    weight="regular"
                    variant="overline"
                  >
                    {{ t('option.symbolList') }}
                  </NText>
                </div>
                <NListboxOption
                  v-for="item in symbols"
                  :key="item.instrumentIsin"
                  class="text-right"
                  :value="item.instrumentIsin"
                  :label="item.instrumentName"
                >
                  {{ item.instrumentName }}
                </NListboxOption>
              </NListbox>
            </div>
          </VeeField>

          <div class="mt-4 w-full">
            <NButton
              v-gid="{
                module: 'option',
                screen: 'home',
                section: 'freezeUnfreezeDialog',
                component: 'submitButton',
                disabled:
                  !meta.valid || !selectedIsin || isPendingFreezeUnfreezeSymbol,
              }"
              variant="fill"
              type="submit"
              color="green"
              class="w-full"
              :disabled="
                !meta.valid || !selectedIsin || isPendingFreezeUnfreezeSymbol
              "
            >
              {{ t('common.submit_request') }}
            </NButton>
          </div>
        </div>
      </form>
    </VeeForm>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLivePortfolioQuery } from '@core/portfolio';
import {
  OPTION_QUERY_KEYS,
  useFreezeUnFreezeQuery,
  FREEZE_UNFREEZE_PARAMS,
} from '@core/option';
import { useQueryClient } from '@libs/use';
import { useToast } from '@negar/core';

type TFreezeUnfreezeDialogProps = {
  isFreeze: boolean;
};

const props = defineProps<TFreezeUnfreezeDialogProps>();

const { t } = useI18n();
const toast = useToast();

const queryClient = useQueryClient();

const { symbols, isLoadingSymbols } = useLivePortfolioQuery();

const selectedIsin = ref({ label: '', value: '' });

const { freezeUnfreezeSymbol, isPendingFreezeUnfreezeSymbol } =
  useFreezeUnFreezeQuery({
    onSuccess: () => {
      queryClient.invalidateQueries(
        OPTION_QUERY_KEYS.frozenHistory(toRef({ isFreeze: props.isFreeze }))
      );

      toast.neutral({
        message: t('option.submitFreezeUnfreezeMessage', {
          type: props.isFreeze ? t('option.freeze') : t('option.unFreeze'),
          symbolName: selectedIsin.value?.label,
        }),
        duration: 3000,
      });

      selectedIsin.value = { label: '', value: '' };
    },
    onReject(error) {
      const message = error.response?.data[0].message;

      if (message) {
        toast.negative({
          message,
          duration: 3000,
        });
      }
    },
  });

const onSubmit = () => {
  freezeUnfreezeSymbol({
    isFreeze: props.isFreeze,
    formModel: {
      isin: selectedIsin.value.value,
      requestGatewayId: FREEZE_UNFREEZE_PARAMS.requestGatewayId,
      executorBrokerBranchId: FREEZE_UNFREEZE_PARAMS.executorBrokerBranchId,
    },
  });
};
</script>
